// O CLIENTE DO ASSISTENTE DE IA — `GET/POST /assistente` no backend (ticket 0045).
//
// O backend conversa e devolve PROPOSTAS: o formulário da história, e ações do catálogo do HQ-Lab
// (`roteirizar`, `gerar_imagem`, `criar_rascunho`, `abrir_tela`). Nada é gravado por ele. Quem
// executa é a tela, com a sessão da criança, e só no clique de "Aprovar".
//
// O que a tela manda é curto: o modo, um contexto pequeno (o rascunho já escrito, a tela atual) e
// as falas. A instrução de sistema é montada no servidor e nunca sai daqui.

import { auth } from "./firebase.class";

export type ModoDoAssistente = "criar-historia" | "criar-personagem" | "agir" | "ajuda";

export interface FalaDoAssistente {
  papel: "pessoa" | "assistente";
  texto: string;
}

export interface PropostaDeAcao {
  acao: string;
  argumentos: Record<string, string | number | boolean>;
  porque?: string;
  exigeAprovacao: true;
}

export interface PropostasDoAssistente {
  formulario?: Record<string, string>;
  acoes?: PropostaDeAcao[];
}

export interface RespostaDoAssistente {
  ok: boolean;
  texto: string;
  propostas: PropostasDoAssistente;
  /** Quando `ok` é falso: a frase pronta para a criança, já escrita pelo servidor. */
  aviso?: string;
}

const ENDERECO = () => `${process.env.REACT_APP_BACKEND_ROUTE}/assistente`;

/** Sem chave no servidor, ele diz `disponivel:false` sem gastar chamada nenhuma. */
export async function assistenteDisponivel(buscar: typeof fetch = fetch): Promise<boolean> {
  try {
    const r = await buscar(ENDERECO());
    if (!r.ok) return false;
    const c = (await r.json()) as { disponivel?: unknown };
    return c.disponivel === true;
  } catch {
    return false;
  }
}

const FALHA =
  "O assistente não conseguiu responder agora. Tente de novo em alguns minutos.";

export async function conversar(
  pedido: { modo: ModoDoAssistente; contexto?: Record<string, string>; falas: FalaDoAssistente[] },
  buscar: typeof fetch = fetch
): Promise<RespostaDoAssistente> {
  const idToken = auth?.currentUser ? await auth.currentUser.getIdToken() : null;
  if (!idToken) {
    return { ok: false, texto: "", propostas: {}, aviso: "Entre na sua conta para conversar com o assistente." };
  }
  try {
    const r = await buscar(ENDERECO(), {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify(pedido),
    });
    const c = (await r.json().catch(() => ({}))) as Partial<RespostaDoAssistente>;
    // O servidor sempre devolve uma frase pronta (`aviso`) quando recusa — moderação, cota, sem
    // chave. Ela é o que se mostra; erro cru de rede nunca chega à criança.
    if (!r.ok || c.ok !== true) {
      return { ok: false, texto: "", propostas: {}, aviso: typeof c.aviso === "string" && c.aviso ? c.aviso : FALHA };
    }
    return { ok: true, texto: c.texto ?? "", propostas: c.propostas ?? {} };
  } catch {
    return { ok: false, texto: "", propostas: {}, aviso: FALHA };
  }
}

/** O nome da prateleira de imagem que o backend entende (`/generate?tipo=`). */
export const TIPO_DA_IMAGEM: Record<string, string> = {
  personagem: "characters",
  cenario: "scenarios",
  figura: "figures",
};

/** As telas que `abrir_tela` pode propor, e o endereço de cada uma. Nada fora daqui abre. */
export const ENDERECO_DA_TELA: Record<string, string> = {
  "meus-quadrinhos": "/meus-quadrinhos",
  "novo-quadrinho": "/meus-quadrinhos/novo",
  roteirizar: "/roteirizar",
  biblioteca: "/biblioteca",
};
